/**
 * Beta / soft-rollback toggle keys.
 *
 * Extracted out of `screens/Beta.tsx` so non-React code (selectors, plain utils)
 * can reference a key without importing a screen component. `Beta.tsx`
 * re-exports both maps, so existing imports keep working.
 */
export const BETA_TOGGLES = {
  notifications: {
    key: 'betaNotificationsEnabled',
  },
  smartOnboardingAIAssistant: {
    key: 'betaSmartOnboardingAIAssistantEnabled',
  },
  newServiceCourseFlow: {
    key: 'betaNewServiceCourseFlowEnabled',
  },
  gravityBasedApps: {
    key: 'betaGravityBasedAppsEnabled',
  },
  /**
   * TEMPORARY — remove once the WSB appointment-limit upsell has been tested.
   *
   * Gates the upsell's UI: the card in the Bookings side menu, the dialog when
   * the allowance is nearly or fully spent, and the banner the appointment
   * micro-apps render.
   *
   * It will not gate the `/usage` request that feeds them, so deleting it
   * changes what is shown, never what is fetched.
   */
  bookingsUpgrade: {
    key: 'betaBookingsUpgradeEnabled',
  },
};

export const SOFT_ROLLBACK_TOGGLES = {
  mainMenu: {
    key: 'softRollbackMainMenuEnabled',
  },
};

/**
 * Reads a beta toggle straight out of localStorage.
 *
 * React code should keep using `useLocalStorageState` so it re-renders when the
 * Beta page flips a switch. This helper exists for the places that are not
 * hooks — chiefly selectors, which run outside React and read the value once per
 * shell load. Toggling a switch therefore needs a reload to reach those
 * consumers.
 *
 * `useLocalStorageState` stores values JSON-encoded, hence `JSON.parse` rather
 * than a `=== 'true'` comparison.
 */
export function isBetaToggleEnabled(key: string): boolean {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) === true : false;
  } catch {
    return false;
  }
}
